/**
 * Configuração de API URLs
 * 
 * Utiliza variáveis de ambiente para configurar a URL base da API
 * - Em produção: usa config.json gerado em runtime (AWS Secrets Manager)
 * - Em desenvolvimento: fallback para localhost:8000
 * 
 * NOTA: Para requisições assíncronas que precisam do token, use apiService.ts
 * Este arquivo mantém os endpoints para compatibilidade.
 */

import { getConfigSync, loadRuntimeConfig } from './runtimeConfig';

// URL base da API - usa variável de ambiente ou fallback para desenvolvimento
// Para uso síncrono (inicialização), use variável de ambiente
const BASE_API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// Garantir que não tenha barra no final
const API_BASE = BASE_API_URL.replace(/\/$/, '');

/**
 * URLs da API organizadas por funcionalidade
 * NOTA: Estes endpoints usam a URL definida em build-time.
 * Para URLs dinâmicas, use apiService.getApiBase()
 */
export const API_ENDPOINTS = {
  // Registrations endpoints
  REGISTRATIONS: `${API_BASE}/registrations/`,
  REGISTRATION_PHOTOS: `${API_BASE}/registrations/photos/`,
  
  // TempDB endpoints
  TEMP_DB: `${API_BASE}/tempdb/`,
  TEMP_DB_ADD_ID: `${API_BASE}/tempdb/add-id/`,
  TEMP_DB_CLEAR: `${API_BASE}/tempdb/clear/`,
  
  // API endpoints
  API_ENVIAR: `${API_BASE}/api/enviar/`,
  API_GETBYID: `${API_BASE}/api/getbyid/`,
  API_CHECKIN: `${API_BASE}/api/checkin/`,
  API_CAIXAUSER: `${API_BASE}/api/caixauser/`,
  API_VIDEO: `${API_BASE}/api/video/`,
  API_CSRF_TOKEN: `${API_BASE}/api/csrf-token/`,
  GET_ALL_REGISTRATIONS: `${API_BASE}/api/getAllRegistrations/`,
  GET_PARAMETERS: `${API_BASE}/api/getParameters/`,
  GET_PHRASES: `${API_BASE}/api/getPhrases/`,
  TUNEL_USER: `${API_BASE}/api/tuneluser/`,

  // Registrations by event
  REGISTRATIONS_BY_EVENT: `${API_BASE}/registrations/by-event`,

  // Health check
  HEALTH: `${API_BASE}/health/`,
};

/**
 * Gera a URL para buscar registrations por event_id
 * @param eventId - ID do evento
 * @param namesOnly - Se true, retorna apenas os nomes (default: true)
 * @returns URL completa para o endpoint
 */
export const getRegistrationsByEventUrl = (eventId: number, namesOnly: boolean = true): string => {
  const url = `${API_ENDPOINTS.REGISTRATIONS_BY_EVENT}/${eventId}`;
  return namesOnly ? `${url}?names_only=true` : url;
};

/**
 * Gera a URL para buscar próximo usuário do túnel
 * @param nameOnly - Se true, retorna apenas o nome do usuário (default: false)
 * @returns URL completa para o endpoint
 */
export const getTunelUserUrl = (nameOnly: boolean = false): string => {
  return nameOnly ? `${API_ENDPOINTS.TUNEL_USER}?name_only=true` : API_ENDPOINTS.TUNEL_USER;
};

/**
 * Configuração para fetch requests com autenticação
 */
export const getApiConfig = async () => {
  const config = getConfigSync();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  
  if (config.apiToken) {
    headers['Authorization'] = `Token ${config.apiToken}`;
  }
  
  return {
    headers,
    timeout: 10000,
  };
};

/**
 * Configuração básica (sem token) para compatibilidade
 */
export const API_CONFIG = {
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 10000,
};

/**
 * Função helper para fazer requests com configuração padrão e autenticação
 * @param endpoint - URL do endpoint
 * @param options - Opções do fetch (RequestInit)
 * @param silentStatusCodes - Array de status codes que não devem logar erro (ex: [404])
 */
export const apiRequest = async (endpoint: string, options: RequestInit = {}, silentStatusCodes: number[] = []) => {
  const config = getConfigSync();
  const authHeaders: Record<string, string> = {};
  
  if (config.apiToken) {
    authHeaders['Authorization'] = `Token ${config.apiToken}`;
  }

  const mergedConfig = {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...authHeaders,
      ...(options.headers || {}),
    },
  };

  try {
    const response = await fetch(endpoint, mergedConfig);
    
    // Se o status está na lista de silenciosos, retorna sem logar/lançar erro
    if (silentStatusCodes.includes(response.status)) {
      return response;
    }
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return response;
  } catch (error) {
    console.error('API Request failed:', error);
    throw error;
  }
};

/**
 * Inicializa a configuração de runtime.
 * Deve ser chamado no início da aplicação.
 */
export const initializeApiConfig = async () => {
  await loadRuntimeConfig();
  console.log('[API] Configuração inicializada');
};


